import throttle from 'lodash-es/throttle';
import {
  isEnoughForTargetAppVersion,
  platform,
  web2app,
} from '@kcd/app-interface';
import { trackEvent } from '@kcd/event-tracker';

import { ENV } from '~/utils/env';

export function openDialogAsync(
  options: Parameters<(typeof web2app)['openDialogAsync']>[0],
) {
  return web2app.openDialogAsync(options);
}

export const openPageModal: (path: string, fallback?: () => void) => void =
  throttle((path: string, fallback?: () => void) => {
    const url = decodeURIComponent(
      new URL(`${window.location.origin}${path}`).toString(),
    );

    web2app.openModal({
      url,
      fallback,
    });
  }, 300);

export const openModal: ({
  url,
  externalBrowser,
}: {
  url: string;
  externalBrowser?: boolean;
}) => void = throttle(
  ({ url, externalBrowser }: { url: string; externalBrowser?: boolean }) => {
    if (externalBrowser) {
      web2app.openExternalBrowser({ url });
      return;
    }

    if (
      url.includes('market.cashnote.kr') ||
      url.includes('stg-market.cashnote.kr')
    ) {
      const urlInstance = new URL(url);

      web2app.openModal({
        url: urlInstance.toString(),
      });
      return;
    }

    web2app.openModal({
      url,
    });
  },
  300,
);

export function closeCurrentModal() {
  web2app.closeCurrentModal({ fallback: () => window.close() });
}

export function appAlert(options?: { title?: string; description?: string }) {
  const description =
    options?.description ||
    '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';

  web2app.openDialog({
    type: 'alert',
    title: options?.title,
    description,
    // eslint-disable-next-line no-alert
    fallback: () => window.alert(description),
  });
}
export function appConfirm(options?: {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  callback?: () => void | Promise<void>;
}) {
  const description =
    options?.description ||
    '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';

  web2app.openDialog({
    type: 'confirm',
    description,
    ...options,

    // eslint-disable-next-line no-alert
    fallback: () => window.alert(description),
  });
}

export const MARKET_TAB_APP_SUPPORT_VERSION = {
  android: '2.2221.109',
  ios: '2.2222.211',
};

export function isLoadedInAppHavingMarketTab() {
  const currentPlatform = platform();
  if (!(currentPlatform === 'ios' || currentPlatform === 'android')) {
    return false;
  }

  return isEnoughForTargetAppVersion(
    MARKET_TAB_APP_SUPPORT_VERSION[currentPlatform],
  );
}

export function isVisibleMoreMenu() {
  const currentPlatform = platform();
  // 웹뷰에서만 노출
  if (!(currentPlatform === 'ios' || currentPlatform === 'android')) {
    return false;
  }

  // iOS 2.2450.851 버전부터 더보기 메뉴 앱 인터페이스 추가됌
  if (currentPlatform === 'ios' && !isEnoughForTargetAppVersion('2.2449.851')) {
    return false;
  }

  return true;
}

export const APP_NAME = {
  ios: 'CashNote Ifrit',
  android: 'CashNote Albatross',
} as const;

export function getCashnoteAppVersion(
  userAgent: string,
  appPlatform: keyof typeof APP_NAME,
) {
  const result = new RegExp(`${APP_NAME[appPlatform]}/(\\d+.\\d+.\\d+)`).exec(
    userAgent,
  );

  if (!result) {
    return null;
  }

  return result[1];
}

const DEV_CASHNOTE_AUTH_URL = 'https://dev-app.cashnote.kr/auth';
const CASHNOTE_AUTH_URL = 'https://app.cashnote.kr/auth';

export const getAuthUrl = (redirectUrl?: string) => {
  const url = ENV === 'dev' ? DEV_CASHNOTE_AUTH_URL : CASHNOTE_AUTH_URL;

  const currentHref = redirectUrl || window.location.href;

  return `${url}?returnUrl=${currentHref}&utm_source=system&utm_medium=login&utm_campaign=market_241118_login`;
};

export function openAllMenu() {
  trackEvent('clicked header menu', {
    name: '전체메뉴',
    page: '마켓',
  });

  web2app.openAllMenu();
}
